import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import Loader from '../components/Loader';
import {
  FiFilm, FiYoutube, FiToggleLeft, FiToggleRight, FiTrash2,
  FiPlus, FiStar, FiEdit2, FiCheck, FiX, FiLogOut,
  FiAlertTriangle, FiBell, FiLayers, FiInbox,
} from 'react-icons/fi';

// ── tiny helpers ────────────────────────────────────────────────────
const inputSx = {
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
  color: 'var(--text)', borderRadius: 12, padding: '10px 14px',
  fontSize: 14, fontFamily: 'inherit', width: '100%', outline: 'none',
};
const focusSx = (e) => (e.target.style.borderColor = 'rgba(229,9,20,0.55)');
const blurSx  = (e) => (e.target.style.borderColor = 'rgba(255,255,255,0.08)');

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-accent uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const MOVIE_EMPTY = { title:'', description:'', releaseYear:'', youtubeId:'', posterUrl:'', backdropUrl:'', genre:'', rating:'' };

// ── Tab IDs ─────────────────────────────────────────────────────────
const TABS = [
  { id:'movies',   icon: FiFilm,   label:'Movies' },
  { id:'add',      icon: FiPlus,   label:'Add Movie' },
  { id:'series',   icon: FiLayers, label:'Series' },
  { id:'requests', icon: FiInbox,  label:'Requests' },
  { id:'notify',   icon: FiBell,   label:'Notifications' },
];

const AdminPage = () => {
  const { user, isAdmin, logOut } = useAuth();
  const navigate = useNavigate();

  const [tab,          setTab]          = useState('movies');
  const [movies,       setMovies]       = useState([]);
  const [requests,     setRequests]     = useState([]);
  const [series,       setSeries]       = useState([]);
  const [notifications,setNotifications]= useState([]);
  const [loadingMovies,setLoadingMovies]= useState(true);
  const [form,         setForm]         = useState(MOVIE_EMPTY);
  const [editId,       setEditId]       = useState(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // notification form
  const [notifTitle,   setNotifTitle]   = useState('');
  const [notifBody,    setNotifBody]    = useState('');
  const [notifSending, setNotifSending] = useState(false);

  useEffect(() => { if (!isAdmin) navigate('/'); }, [isAdmin, navigate]);

  // movies
  useEffect(() => {
    const q = query(collection(db, 'customMovies'), orderBy('createdAt','desc'));
    return onSnapshot(q, s => { setMovies(s.docs.map(d=>({id:d.id,...d.data()}))); setLoadingMovies(false); });
  }, []);

  // requests
  useEffect(() => {
    const q = query(collection(db, 'movieRequests'), orderBy('createdAt','desc'));
    return onSnapshot(q, s => setRequests(s.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  // series
  useEffect(() => {
    const q = query(collection(db, 'series'), orderBy('createdAt','desc'));
    return onSnapshot(q, s => setSeries(s.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  // notifications
  useEffect(() => {
    const q = query(collection(db, 'notifications'), orderBy('createdAt','desc'));
    return onSnapshot(q, s => setNotifications(s.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  // ── Movie CRUD ────────────────────────────────────────────────────
  const f = k => e => setForm(p=>({...p,[k]:e.target.value}));

  const submitMovie = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.youtubeId.trim()) return;
    setSubmitting(true);
    const data = {
      ...form,
      rating: parseFloat(form.rating)||0,
      releaseYear: parseInt(form.releaseYear)||new Date().getFullYear(),
      source:'custom',
      featured: editId ? movies.find(m=>m.id===editId)?.featured??false : false,
    };
    try {
      if (editId) await updateDoc(doc(db,'customMovies',editId),{...data,updatedAt:serverTimestamp()});
      else        await addDoc(collection(db,'customMovies'),{...data,createdAt:serverTimestamp()});
      setForm(MOVIE_EMPTY); setEditId(null); setTab('movies');
    } catch(err){ console.error(err); }
    finally { setSubmitting(false); }
  };

  const toggleFeatured = id => {
    const m = movies.find(x=>x.id===id);
    if(m) updateDoc(doc(db,'customMovies',id),{featured:!m.featured});
  };

  const startEdit = m => {
    setForm({ title:m.title||'', description:m.description||'', releaseYear:String(m.releaseYear||''),
      youtubeId:m.youtubeId||'', posterUrl:m.posterUrl||'', backdropUrl:m.backdropUrl||'',
      genre:m.genre||'', rating:String(m.rating||'') });
    setEditId(m.id); setTab('add');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, deleteTarget.col, deleteTarget.id));
    setDeleteTarget(null);
  };

  // ── Request update ────────────────────────────────────────────────
  const updateRequest = (id, status) => updateDoc(doc(db,'movieRequests',id),{status});

  // ── Send notification ─────────────────────────────────────────────
  const sendNotif = async (e) => {
    e.preventDefault();
    if (!notifTitle.trim()) return;
    setNotifSending(true);
    try {
      await addDoc(collection(db,'notifications'),{
        title:notifTitle.trim(), body:notifBody.trim(),
        global:true, createdAt:serverTimestamp(),
      });
      setNotifTitle(''); setNotifBody('');
    } finally { setNotifSending(false); }
  };

  if (!isAdmin) return null;
  const featured = movies.filter(m=>m.featured);

  return (
    <div style={{ background:'var(--dark)', minHeight:'100vh', paddingTop:88 }}>
      <div className="max-w-screen-xl mx-auto px-6 md:px-12 pb-16">

        {/* Header */}
        <motion.div initial={{opacity:0,y:-16}} animate={{opacity:1,y:0}}
          className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{background:'var(--red)'}}/>
              <span className="text-xs font-accent uppercase tracking-widest" style={{color:'var(--red)'}}>Admin Panel</span>
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-wide" style={{color:'var(--text)'}}>CineSpur Dashboard</h1>
            <p className="text-sm mt-1 font-body" style={{color:'var(--muted)'}}>
              Signed in as <span style={{color:'var(--text)'}}>{user?.email}</span>
            </p>
          </div>
          <motion.button whileHover={{scale:1.05}} whileTap={{scale:0.95}}
            onClick={()=>{logOut();navigate('/');}}
            className="flex items-center gap-2 px-4 py-2 rounded-xl glass text-sm font-body"
            style={{color:'var(--muted)'}}>
            <FiLogOut size={15}/> Sign Out
          </motion.button>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            {label:'Custom Movies', value:movies.length,             icon:FiFilm,  color:'#E50914'},
            {label:'Featured',      value:featured.length,           icon:FiStar,  color:'#F5C518'},
            {label:'Requests',      value:requests.filter(r=>r.status==='pending').length, icon:FiInbox,color:'#00C9FF'},
            {label:'Series',        value:series.length,             icon:FiLayers,color:'#BD10E0'},
          ].map(s=>(
            <motion.div key={s.label} initial={{opacity:0,y:12}} animate={{opacity:1,y:0}}
              className="rounded-2xl p-5 glass">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-accent uppercase tracking-wider" style={{color:'var(--muted)'}}>{s.label}</span>
                <s.icon size={16} style={{color:s.color}}/>
              </div>
              <p className="font-display text-3xl" style={{color:'var(--text)'}}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Tab bar */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(t=>(
            <motion.button key={t.id} whileHover={{scale:1.02}} whileTap={{scale:0.98}}
              onClick={()=>{setTab(t.id); if(t.id==='movies'){setForm(MOVIE_EMPTY);setEditId(null);}}}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body font-medium"
              style={{
                background: tab===t.id ? 'var(--red)' : 'rgba(255,255,255,0.04)',
                color:      tab===t.id ? '#fff'       : 'var(--muted)',
                border:`1px solid ${tab===t.id?'rgba(229,9,20,0.5)':'rgba(255,255,255,0.07)'}`,
                boxShadow:  tab===t.id ? '0 0 18px rgba(229,9,20,0.25)' : 'none',
              }}>
              <t.icon size={14}/> {t.label}
              {t.id==='requests' && requests.filter(r=>r.status==='pending').length>0 && (
                <span className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-accent"
                  style={{background:'#F5C518',color:'#000',fontSize:9}}>
                  {requests.filter(r=>r.status==='pending').length}
                </span>
              )}
            </motion.button>
          ))}
        </div>

        {/* ── MOVIES tab ── */}
        {tab==='movies' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            {loadingMovies ? <Loader/> : movies.length===0 ? (
              <div className="flex flex-col items-center py-24 gap-4">
                <FiFilm size={44} style={{color:'var(--border)'}}/>
                <p style={{color:'var(--muted)'}} className="font-body">No custom movies yet.</p>
                <motion.button whileHover={{scale:1.04}} whileTap={{scale:0.96}}
                  onClick={()=>setTab('add')}
                  className="px-6 py-3 rounded-xl text-sm font-body font-semibold"
                  style={{background:'var(--red)',color:'#fff'}}>
                  Add First Movie
                </motion.button>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {movies.map((movie,i)=>(
                  <motion.div key={movie.id} layout
                    initial={{opacity:0,x:-14}} animate={{opacity:1,x:0}} transition={{delay:i*0.03}}
                    className="flex items-center gap-4 rounded-2xl p-4"
                    style={{
                      background:'rgba(17,17,24,0.85)',
                      border: movie.featured ? '1px solid rgba(245,197,24,0.3)' : '1px solid rgba(255,255,255,0.05)',
                    }}>
                    <div className="flex-shrink-0 w-11 h-16 rounded-lg overflow-hidden" style={{background:'var(--card)'}}>
                      {movie.posterUrl
                        ? <img src={movie.posterUrl} alt="" className="w-full h-full object-cover"/>
                        : <div className="w-full h-full flex items-center justify-center"><FiFilm size={16} style={{color:'var(--muted)'}}/></div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-lg leading-tight" style={{color:'var(--text)'}}>{movie.title}</h3>
                        {movie.featured && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-accent"
                            style={{background:'rgba(245,197,24,0.12)',color:'var(--gold)',border:'1px solid rgba(245,197,24,0.25)'}}>
                            ★ Hero
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        {movie.releaseYear && <span className="text-xs" style={{color:'var(--muted)'}}>{movie.releaseYear}</span>}
                        {movie.genre      && <span className="text-xs" style={{color:'var(--muted)'}}>{movie.genre}</span>}
                        {movie.youtubeId  && <span className="flex items-center gap-1 text-xs" style={{color:'#ff4444'}}><FiYoutube size={10}/> YT</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                        onClick={()=>toggleFeatured(movie.id)}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-body"
                        title={movie.featured?'Unpin from Hero':'Pin to Hero'}
                        style={{
                          background: movie.featured?'rgba(245,197,24,0.12)':'rgba(255,255,255,0.05)',
                          color:      movie.featured?'var(--gold)':'var(--muted)',
                          border:`1px solid ${movie.featured?'rgba(245,197,24,0.3)':'rgba(255,255,255,0.07)'}`,
                        }}>
                        {movie.featured?<FiToggleRight size={14}/>:<FiToggleLeft size={14}/>}
                        <span className="hidden sm:inline">{movie.featured?'Unpin':'Pin'}</span>
                      </motion.button>
                      <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                        onClick={()=>startEdit(movie)}
                        className="w-9 h-9 rounded-lg glass flex items-center justify-center hover:bg-white/10"
                        style={{color:'var(--text)'}}>
                        <FiEdit2 size={13}/>
                      </motion.button>
                      <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                        onClick={()=>setDeleteTarget({col:'customMovies',id:movie.id,label:movie.title})}
                        className="w-9 h-9 rounded-lg glass flex items-center justify-center hover:bg-red-900/30"
                        style={{color:'var(--muted)'}}>
                        <FiTrash2 size={13}/>
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ── ADD/EDIT tab ── */}
        {tab==='add' && (
          <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}
            className="rounded-2xl p-6 md:p-8"
            style={{background:'rgba(17,17,24,0.88)',border:'1px solid rgba(255,255,255,0.07)'}}>
            <h2 className="font-display text-2xl mb-6" style={{color:'var(--text)'}}>
              {editId?'Edit Movie':'Add Custom Movie'}
            </h2>
            <form onSubmit={submitMovie}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                {[
                  ['title','Movie Title *','e.g. The Grand Heist'],
                  ['genre','Genre','e.g. Action, Drama'],
                  ['releaseYear','Release Year','2024'],
                  ['rating','Rating (0-10)','7.5'],
                  ['posterUrl','Poster Image URL','https://…'],
                  ['backdropUrl','Backdrop Image URL','https://…'],
                ].map(([key,label,ph])=>(
                  <Field key={key} label={label}>
                    <input style={inputSx} placeholder={ph} value={form[key]} onChange={f(key)} required={key==='title'}
                      onFocus={focusSx} onBlur={blurSx}
                      type={['releaseYear','rating'].includes(key)?'number':'text'}
                      step={key==='rating'?'0.1':undefined}
                      min={key==='rating'?0:key==='releaseYear'?1900:undefined}
                      max={key==='rating'?10:key==='releaseYear'?2099:undefined} />
                  </Field>
                ))}
                <Field label="YouTube Video ID *">
                  <div className="relative">
                    <FiYoutube size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{color:'#ff4444'}}/>
                    <input style={{...inputSx,paddingLeft:36}} placeholder="e.g. dQw4w9WgXcQ"
                      value={form.youtubeId} onChange={f('youtubeId')} required
                      onFocus={focusSx} onBlur={blurSx}/>
                  </div>
                  {form.youtubeId && (
                    <a href={`https://youtu.be/${form.youtubeId}`} target="_blank" rel="noreferrer"
                      className="text-xs underline mt-0.5" style={{color:'var(--muted)'}}>Preview →</a>
                  )}
                </Field>
              </div>
              <Field label="Description">
                <textarea style={{...inputSx,resize:'vertical',minHeight:90}}
                  placeholder="Movie synopsis…" value={form.description} onChange={f('description')}
                  onFocus={focusSx} onBlur={blurSx}/>
              </Field>
              <div className="flex items-center gap-3 mt-6">
                <motion.button whileHover={{scale:1.03}} whileTap={{scale:0.97}} type="submit" disabled={submitting}
                  className="flex items-center gap-2 px-7 py-3 rounded-xl font-body font-semibold text-sm"
                  style={{background:'var(--red)',color:'#fff',boxShadow:'0 0 22px rgba(229,9,20,0.3)',opacity:submitting?0.6:1}}>
                  {submitting?'Saving…':editId?<><FiCheck size={14}/>Update</>:<><FiPlus size={14}/>Add Movie</>}
                </motion.button>
                {editId && (
                  <motion.button type="button" whileHover={{scale:1.03}} whileTap={{scale:0.97}}
                    onClick={()=>{setForm(MOVIE_EMPTY);setEditId(null);}}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-body text-sm glass hover:bg-white/10"
                    style={{color:'var(--muted)'}}>
                    <FiX size={13}/>Cancel
                  </motion.button>
                )}
              </div>
            </form>
          </motion.div>
        )}

        {/* ── SERIES tab ── */}
        {tab==='series' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            <div className="rounded-2xl p-6 mb-4"
              style={{background:'rgba(17,17,24,0.85)',border:'1px solid rgba(255,255,255,0.07)'}}>
              <p className="font-body text-sm mb-4" style={{color:'var(--muted)'}}>
                Series are created via Firestore directly. Add a document to the <code className="px-1.5 py-0.5 rounded text-xs" style={{background:'rgba(255,255,255,0.08)',color:'var(--red)'}}>series</code> collection, then add <code className="px-1.5 py-0.5 rounded text-xs" style={{background:'rgba(255,255,255,0.08)',color:'var(--red)'}}>seasons</code> and <code className="px-1.5 py-0.5 rounded text-xs" style={{background:'rgba(255,255,255,0.08)',color:'var(--red)'}}>episodes</code> sub-collections.
              </p>
              <div className="rounded-xl p-4 font-accent text-xs" style={{background:'rgba(0,0,0,0.4)',color:'#7dd3fc',lineHeight:1.8}}>
                {`series/{id}\n  title, description, genre, releaseYear\n  posterUrl, backdropUrl\n  → seasons/{id}\n      title, order\n      → episodes/{id}\n          title, description, youtubeId\n          duration, thumbnailUrl, order`}
              </div>
            </div>
            {series.map(s=>(
              <div key={s.id} className="flex items-center justify-between rounded-2xl px-5 py-4 mb-3"
                style={{background:'rgba(17,17,24,0.8)',border:'1px solid rgba(255,255,255,0.05)'}}>
                <div>
                  <p className="font-display text-lg" style={{color:'var(--text)'}}>{s.title}</p>
                  <p className="text-xs mt-0.5" style={{color:'var(--muted)'}}>{s.genre} · {s.releaseYear}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Link to={'/series/'+s.id}>
                    <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                      className="px-3 py-1.5 rounded-lg glass text-xs font-body hover:bg-white/10" style={{color:'var(--text)'}}>
                      View
                    </motion.button>
                  </Link>
                  <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                    onClick={()=>setDeleteTarget({col:'series',id:s.id,label:s.title})}
                    className="w-8 h-8 rounded-lg glass flex items-center justify-center hover:bg-red-900/30" style={{color:'var(--muted)'}}>
                    <FiTrash2 size={13}/>
                  </motion.button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {/* ── REQUESTS tab ── */}
        {tab==='requests' && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}>
            {requests.length===0 ? (
              <div className="flex flex-col items-center py-20 gap-3">
                <FiInbox size={44} style={{color:'var(--border)'}}/>
                <p style={{color:'var(--muted)'}} className="font-body">No requests yet.</p>
              </div>
            ) : requests.map((req,i)=>(
              <motion.div key={req.id} initial={{opacity:0,x:-14}} animate={{opacity:1,x:0}} transition={{delay:i*0.03}}
                className="flex items-center gap-4 rounded-2xl px-5 py-4 mb-3"
                style={{background:'rgba(17,17,24,0.8)',border:'1px solid rgba(255,255,255,0.05)'}}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body font-semibold" style={{color:'var(--text)'}}>{req.title}</p>
                  {req.note && <p className="text-xs mt-0.5 line-clamp-1" style={{color:'var(--muted)'}}>{req.note}</p>}
                  <p className="text-xs mt-0.5 font-accent" style={{color:'var(--muted)',opacity:0.6}}>
                    by {req.displayName} · {req.createdAt?new Date(req.createdAt.seconds*1000).toLocaleDateString():''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {req.status==='pending' && (
                    <>
                      <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                        onClick={()=>updateRequest(req.id,'approved')}
                        className="px-3 py-1.5 rounded-lg text-xs font-body font-medium"
                        style={{background:'rgba(0,200,100,0.15)',color:'#00C864',border:'1px solid rgba(0,200,100,0.3)'}}>
                        Approve
                      </motion.button>
                      <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                        onClick={()=>updateRequest(req.id,'rejected')}
                        className="px-3 py-1.5 rounded-lg text-xs font-body font-medium"
                        style={{background:'rgba(229,9,20,0.12)',color:'var(--red)',border:'1px solid rgba(229,9,20,0.3)'}}>
                        Reject
                      </motion.button>
                    </>
                  )}
                  {req.status!=='pending' && (
                    <span className="text-xs font-accent capitalize px-3 py-1.5 rounded-lg"
                      style={{background:'rgba(255,255,255,0.06)',color:'var(--muted)'}}>
                      {req.status}
                    </span>
                  )}
                  <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                    onClick={()=>setDeleteTarget({col:'movieRequests',id:req.id,label:req.title})}
                    className="w-8 h-8 rounded-lg glass flex items-center justify-center hover:bg-red-900/30" style={{color:'var(--muted)'}}>
                    <FiTrash2 size={13}/>
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* ── NOTIFICATIONS tab ── */}
        {tab==='notify' && (
          <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}}>
            <div className="rounded-2xl p-6 mb-6"
              style={{background:'rgba(17,17,24,0.88)',border:'1px solid rgba(255,255,255,0.07)'}}>
              <h3 className="font-display text-xl mb-4" style={{color:'var(--text)'}}>Send Notification</h3>
              <form onSubmit={sendNotif} className="flex flex-col gap-4">
                <Field label="Title *">
                  <input style={inputSx} placeholder="e.g. New movie added!" required
                    value={notifTitle} onChange={e=>setNotifTitle(e.target.value)} onFocus={focusSx} onBlur={blurSx}/>
                </Field>
                <Field label="Body (optional)">
                  <textarea style={{...inputSx,resize:'vertical',minHeight:72}}
                    placeholder="Optional description…"
                    value={notifBody} onChange={e=>setNotifBody(e.target.value)} onFocus={focusSx} onBlur={blurSx}/>
                </Field>
                <motion.button whileHover={{scale:1.03}} whileTap={{scale:0.97}} type="submit" disabled={notifSending}
                  className="flex items-center justify-center gap-2 py-3 rounded-xl font-body font-semibold text-sm"
                  style={{background:'var(--red)',color:'#fff',opacity:notifSending?0.6:1}}>
                  <FiBell size={15}/> {notifSending?'Sending…':'Broadcast to All Users'}
                </motion.button>
              </form>
            </div>
            {/* Past notifications */}
            <div className="flex flex-col gap-3">
              {notifications.map((n,i)=>(
                <motion.div key={n.id} initial={{opacity:0,x:-12}} animate={{opacity:1,x:0}} transition={{delay:i*0.03}}
                  className="flex items-start justify-between gap-3 rounded-xl px-5 py-4"
                  style={{background:'rgba(17,17,24,0.7)',border:'1px solid rgba(255,255,255,0.05)'}}>
                  <div>
                    <p className="text-sm font-body font-semibold" style={{color:'var(--text)'}}>{n.title}</p>
                    {n.body && <p className="text-xs mt-0.5" style={{color:'var(--muted)'}}>{n.body}</p>}
                    <p className="text-xs mt-1 font-accent" style={{color:'var(--muted)',opacity:0.55}}>
                      {n.createdAt?new Date(n.createdAt.seconds*1000).toLocaleString():''}
                    </p>
                  </div>
                  <motion.button whileHover={{scale:1.08}} whileTap={{scale:0.92}}
                    onClick={()=>deleteDoc(doc(db,'notifications',n.id))}
                    className="w-8 h-8 rounded-lg glass flex items-center justify-center flex-shrink-0 hover:bg-red-900/30"
                    style={{color:'var(--muted)'}}>
                    <FiTrash2 size={13}/>
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Delete confirm modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{background:'rgba(0,0,0,0.78)',backdropFilter:'blur(8px)'}}
            onClick={()=>setDeleteTarget(null)}>
            <motion.div initial={{scale:0.84,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.84,opacity:0}}
              onClick={e=>e.stopPropagation()}
              className="rounded-2xl p-7 max-w-sm w-full"
              style={{background:'var(--card)',border:'1px solid rgba(229,9,20,0.35)'}}>
              <div className="flex items-center gap-3 mb-3">
                <FiAlertTriangle size={22} style={{color:'var(--red)'}}/>
                <h3 className="font-display text-xl" style={{color:'var(--text)'}}>Delete?</h3>
              </div>
              <p className="text-sm font-body mb-6" style={{color:'var(--muted)'}}>
                This will permanently remove <strong style={{color:'var(--text)'}}>{deleteTarget.label}</strong>.
              </p>
              <div className="flex gap-3">
                <motion.button whileHover={{scale:1.04}} whileTap={{scale:0.96}} onClick={confirmDelete}
                  className="flex-1 py-2.5 rounded-xl text-sm font-body font-semibold"
                  style={{background:'var(--red)',color:'#fff'}}>Delete</motion.button>
                <motion.button whileHover={{scale:1.04}} whileTap={{scale:0.96}} onClick={()=>setDeleteTarget(null)}
                  className="flex-1 py-2.5 rounded-xl text-sm font-body glass hover:bg-white/10"
                  style={{color:'var(--text)'}}>Cancel</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPage;
